<template>
      <div class="game-board" v-if="safeReady">
        <div class="gb-debug" v-if="!gameStore || !gameStore.playerHand">Loading game…</div>

        <div class="container">
          <!-- Header -->
          <div class="game-header" v-if="gameStore && gameStore.playerHand">
            <div class="game-info">
              <h2 class="mode-title">
                {{ modeTitleI18n }}
                <span v-if="gameStore.isCoop" class="participants">
                  <span class="avatar me" :title="t('game.you')">Y</span>
                  <span class="dot">·</span>
                  <span class="avatar tm" :title="t('game.teammate')">T</span>
                </span>
              </h2>
              <p>
                <!-- 统一显示：回合 当前/总回合（直接拼接，避免 {cur}/{total} 原样输出） -->
                {{ t('game.hud.round') }} {{ gameStore.round }}/{{ gameStore.isCoop ? gameStore.COOP_ROUNDS : gameStore.CLASSIC_ROUNDS }} •
                <template v-if="gameStore.isCoop">
                  <span :class="['turn-pill', gameStore.currentPlayer==='localPlayer' ? 'active' : '']">{{ t('game.you') }}</span>
                  •
                  <span :class="['turn-pill', gameStore.currentPlayer==='teammate' ? 'active' : '']">{{ t('game.teammate') }}</span>
                </template>
                <template v-else>
                  <span class="turn-pill active">{{ t('game.you') }}</span>
                </template>
                <span v-if="['lightning','coop'].includes(gameStore.gameMode)" :class="['timer', gameStore.turnRemaining <= 5 ? 'critical' : '']">
                  ⏱️ {{ gameStore.turnRemaining }}{{ t('game.hud.seconds') }}
                </span>
                <span v-if="gameStore.isCoop" class="coop-target">
                  • {{ t('game.hud.team') }} {{ gameStore.teamScore }}/{{ gameStore.COOP_TARGET }}
                </span>
                <!-- 取消 R 缩写的总回合显示，已在前面以 {cur}/{total} 呈现 -->
                <span class="meta">• {{ t('game.hud.hand') }} {{ gameStore.handSize }}, {{ t('game.hud.deck') }} {{ gameStore.deck.length }}</span>
              </p>

              <div v-if="gameStore.isCoop" class="team-progress">
                <div class="bar"><div class="fill" :style="{ width: teamProgressPct + '%' }"></div></div>
                <div class="meta-line">
                  <span>{{ t('game.progress.target') }} {{ gameStore.COOP_TARGET }}</span>
                  <span>· {{ t('game.progress.rounds') }} {{ gameStore.COOP_ROUNDS - gameStore.round + 1 }}</span>
                </div>
              </div>
            </div>

              <div class="header-right">
                <div class="header-actions">
                  <button class="btn-secondary btn-exit" @click="goHome">{{ t('buttons.backHome') }}</button>
                </div>

              <div class="score-display">
                <template v-if="gameStore.isCoop">
                  <template v-for="(score, player) in (gameStore.scores || {})" :key="player">
                    <div class="score-item">
                      <span class="player-name">{{ player === 'localPlayer' ? t('game.you') : t('game.teammate') }}</span>
                      <span class="player-score">{{ score }}</span>
                    </div>
                  </template>
                </template>
                <template v-else>
                  <div class="score-item">
                    <span class="player-name">{{ t('game.you') }}</span>
                    <span class="player-score">{{ (gameStore.scores && gameStore.scores['localPlayer']) || 0 }}</span>
                  </div>
                </template>
              </div>
              </div>
          </div>

          <!-- Main -->
          <div class="game-main" v-if="gameStore && gameStore.playerHand">
            <div class="left-col">
              <div class="hand-area">
                <h3>{{ t('game.yourHand') }}</h3>
                <div class="cards-container">
                  <div
                    v-for="card in safeHand"
                    :key="card.id"
                    class="mini-card"
                    :class="{ selected: isCardSelected(card) }"
                    @click="selectCard(card)"
                  >
                    <div class="ch">{{ card.letter }}</div>
                    <div class="pv">{{ card.points }}</div>
                  </div>
                </div>
              </div>

              <div class="restart-row-centered">
                <button class="btn-restart" @click="restartGame">{{ t('buttons.restart') }}</button>
              </div>

              <div class="pool-area" v-if="gameStore.isCoop">
                <h3>{{ t('game.publicPool') }}</h3>
                <div class="cards-container">
                  <div class="pool-card" v-for="card in safePool" :key="card.id" @click="selectCard(card)">
                    <div class="mini-card">
                      <div class="ch">{{ card.letter }}</div>
                      <div class="pv">{{ card.points }}</div>
                    </div>
                    <span class="source-badge" v-if="card && card._from === 'hand'">{{ t('game.labels.fromHand') }}</span>
                    <span class="source-badge teammate" v-else-if="card && card._from === 'teammate'">{{ t('game.labels.fromTeammate') }}</span>
                    <span class="source-badge deck" v-else>{{ t('game.labels.fromDeck') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="center-col">
              <div class="selected-area">
                <h3>{{ t('game.selected') }}</h3>
                <div class="selected-cards">
                  <div
                    v-for="(card, idx) in safeSelected"
                    :key="idx + '_' + (card.letter||idx)"
                    class="mini-card selected"
                    @click="removeSelectedCard(idx)"
                  >
                    <div class="ch">{{ card.letter }}</div>
                    <div class="pv">{{ card.points }}</div>
                  </div>
                </div>
                <p class="selected-hint">{{ t('game.selectedHint') }}</p>
                <div v-if="gameStore.isCoop" class="coop-hint under-selected">
                  {{ t('game.coopHint') }}
                </div>
              </div>

              <div class="controls">
                <input class="word-input" v-model="currentWord" :placeholder="t('placeholders.enterWord')" />
                <div class="buttons-row">
                  <button class="btn-primary"
                          :title="!isPlayEnabled ? '请选择与输入匹配的字母或直接输入至少2个字母' : ''"
                          :disabled="!isPlayEnabled"
                          @click="playWord">{{ t('buttons.play') }}</button>
                  <button v-if="gameStore.isCoop" class="btn-secondary" :disabled="!canDonate" @click="donateToPool">{{ t('buttons.donate') }}</button>
                  <button class="btn-pass" @click="passTurn">{{ t('buttons.pass') }}</button>
                </div>

                <!-- Definition Panel -->
                <div class="definition-panel" v-if="definitionState.visible">
                  <div class="def-header">
                    <div class="def-title">{{ t('game.definition') }}</div>
                    <div class="def-meta" v-if="moreState.lastWord">
                      <span class="def-word">{{ moreState.lastWord }}</span>
                      <span class="def-phonetic" v-if="definitionState.phonetic">/{{ definitionState.phonetic }}/</span>
                    </div>
                  </div>

                  <div v-if="definitionState.loading" class="def-loading">{{ t('dict.loading') }}</div>

                  <!-- 收起：首义项 -->
                  <div v-else-if="!moreState.expanded" class="def-body">
                    <div class="def-item">
                      <span v-if="definitionState.firstPos" class="pos-tag" :data-pos="definitionState.firstPos">{{ definitionState.firstPos }}</span>
                      <span class="def-text">{{ definitionState.text }}</span>
                    </div>
                  </div>

                  <!-- 展开：多条 -->
                  <div v-else class="def-body">
                    <div
                      v-for="(d, i) in moreState.all"
                      :key="'def_'+i"
                      class="def-item expanded"
                    >
                      <span class="def-bullet">{{ i + 1 }}</span>
                      <span v-if="d.pos" class="pos-tag" :data-pos="d.pos">{{ mapPos(d.pos) }}</span>
                      <span class="def-text">{{ d.def }}</span>
                    </div>
                  </div>

                  <div class="def-actions" v-if="moreState.lastWord">
                    <button class="btn-link" @click="toggleMore">
                      {{ moreState.expanded ? t('buttons.less') : t('buttons.more') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="right-col">
              <div class="history">
                <h3>{{ t('game.history') }}</h3>
                <ul class="history-list">
                  <li
                    v-for="(item, idx) in histItems"
                    :key="'h_'+idx"
                  >
                    <template v-if="item.type === 'word_played'">
                      <span class="hist-word">
                        <span :class="['actor-tag', item.player==='localPlayer' ? 'me' : 'tm']">
                          {{ item.player==='localPlayer' ? t('game.you') : t('game.teammate') }}
                        </span>
                        {{ item.word }}
                      </span>
                      <span class="hist-score">+{{ item.score }}</span>
                    </template>
                    <template v-else-if="item.type === 'no_play'">
                      <span class="hist-word">
                        <span class="actor-tag tm">{{ t('game.teammate') }}</span>
                        {{ t('game.noPlay') }}
                      </span>
                      <span class="hist-score dim">—</span>
                    </template>
                    <template v-else-if="item.type === 'donate'">
                      <span class="hist-word">
                        <span class="actor-tag me">{{ t('game.you') }}</span>
                        Donated to pool: {{ (item.cards || []).join(', ') }}
                      </span>
                      <span class="hist-score dim">—</span>
                    </template>
                  </li></ul>
              </div>
            </div>
          </div>
        </div><!-- /.container -->

        <!-- 当没有历史记录时给出提示 -->
        <div v-if="gameStore && (!histItems || histItems.length === 0)" class="container" style="margin-top:8px;">
          <p style="color:#98A2B3">暂无历史记录</p>
        </div>

        <div v-if="overlayVisible" class="overlay">
          <div class="overlay-card">
            <h3>{{ overlayTitle }}</h3>
            <p>{{ overlayMessage }}</p>
            <button class="btn-primary" @click="restartGame">{{ t('buttons.restart') }}</button>
          </div>
        </div>
      </div>
      <div v-else class="game-board-fallback">
        <div class="container"><p style="color:#667085">Initializing game…</p></div>
      </div>
    </template>
    <script>
    import { useGameStore } from '../stores/gameStore'
    import { t, onLangChange } from '../utils/i18n.js'

  export default {
  name: 'GameBoard',
  data() {
  return {
  currentWord: '',
  definitionState: { visible: false, loading: false, text: '', firstPos: '', phonetic: '' },
  moreState: { expanded: false, lastWord: '', all: [] },
  definitionCache: Object.create(null),
  pendingDefinitions: Object.create(null),
  }
  },
  computed: {
  gameStore() { return useGameStore() },
  // 放宽出词按钮启用条件：输入框>=2 或 已选字母>=2，且轮到本人且未在结算
  isPlayEnabled(){
    const len = (this.currentWord || '').trim().length
    const sel = this.safeSelected.length
    const yourTurn = !!this.gameStore?.isPlayerTurn
    const resolving = !!this.gameStore?.isResolving
    return (len >= 2 || sel >= 2) && yourTurn && !resolving
  },
  histItems(){
  const store = this.gameStore
  if (!store) return []

  const toArray = (value) => {
  if (Array.isArray(value)) return value
  if (value && Array.isArray(value.value)) return value.value
  return []
  }

  const log = toArray(store.gameLog)
  const historyOnly = toArray(store.gameHistory)
  const source = log && log.length ? log : historyOnly
  if (!source || !source.length) return []
  return source
  .map(item => {
  if (!item) return null
  return {
  ...item,
  player: item.player || 'localPlayer',
  score: typeof item.score === 'number' ? item.score : Number(item.score) || 0,
  cards: Array.isArray(item.cards) ? item.cards : (item.cards ? [item.cards] : []),
  }
  })
  .filter(evt => evt && evt.type && evt.type !== 'game_result')
  .sort((a, b) => (b?.timestamp || 0) - (a?.timestamp || 0))
  },
  safeReady(){
  const s = this.gameStore
  return !!(s && Array.isArray(s.playerHand) && Array.isArray(s.publicPool) && Array.isArray(s.selectedCards))
  },
  safeHand() {
  const a = (this.gameStore?.playerHand || [])
  return a.filter(v => v && typeof v==='object' && v.letter !== undefined)
  },
  safePool() {
  const a = (this.gameStore?.publicPool || [])
  return a.filter(v => v && typeof v==='object' && v.letter !== undefined)
  },
  safeSelected() {
  const a = (this.gameStore?.selectedCards || [])
  return a.filter(v => v && typeof v==='object' && v.letter !== undefined)
  },
  teamProgressPct(){
  if (!this.gameStore || !this.gameStore.isCoop) return 0
  const v = Math.max(0, Math.min(1, this.gameStore.teamScore / this.gameStore.COOP_TARGET))
  return Math.round(v * 100)
  },
  canPlayWord() {
  const w = (this.currentWord || '').trim()
  return w.length >= 2 && !!this.gameStore?.canPlay
  },
  canDonate() {
  const sel = this.safeSelected
  const hand = this.safeHand
  if (!this.gameStore?.isCoop) return false
  if (!sel.length) return false
  return sel.some(c => hand.find(x => x && x.id === c.id))
  },
  overlayVisible() { return this.gameStore?.gameState === 'ended' },
  overlayTitle() {
  if (this.gameStore?.isCoop) return t('game.overlay.resultTeam')
  if (this.gameStore?.gameMode === 'lightning') return t('game.overlay.timeUp')
  return t('game.overlay.gameOver')
  },
  overlayMessage() {
  if (this.gameStore?.isCoop) return 'Great teamwork—play again?'
  if (this.gameStore?.gameMode === 'lightning') return 'Beat the timer next round!'
  return 'Nice run—try another game.'
  },
  modeTitleI18n() {
  if (this.gameStore?.gameMode === 'lightning') return t('modes.lightning')
  if (this.gameStore?.gameMode === 'coop') return t('modes.coop')
  return t('modes.classic')
  },
  },
  watch: {
  currentWord(newVal){
  const word = String(newVal || '').trim().toLowerCase()
  if (this._definitionPrefetchTimer) {
  clearTimeout(this._definitionPrefetchTimer)
  this._definitionPrefetchTimer = null
  }
  if (word.length < 2) return
  this._definitionPrefetchTimer = setTimeout(() => {
  this.prefetchDefinition(word)
  }, 350)
  },
  },
  mounted(){
  try { this._unsubLang = onLangChange(() => this.$forceUpdate()) } catch (e) {}
  this._onHashChange = () => { this.syncModeFromHash() }
  try { window.addEventListener('hashchange', this._onHashChange) } catch (_) {}
  this.syncModeFromHash()
  },
  beforeUnmount(){
  this._unsubLang && this._unsubLang()
  if (this._onHashChange) {
  try { window.removeEventListener('hashchange', this._onHashChange) } catch (_) {}
  this._onHashChange = null
  }
  if (this._definitionPrefetchTimer) {
  clearTimeout(this._definitionPrefetchTimer)
  this._definitionPrefetchTimer = null
  }
  },
  methods: {
  t,
  goHome() { window.location.hash = '#/games/poker' },
  syncModeFromHash(){
  try {
  const store = this.gameStore
  if (!store) return
  const hash = window.location.hash || ''
  if (!hash.startsWith('#/game')) return

  // 支持新的 URL 格式：/games/poker/classic
  let match = hash.match(/\/games\/poker\/(\w+)/)
  let requested = match && match[1] ? match[1] : null

  // 如果没有匹配到，再尝试旧的查询参数格式
  if (!requested) {
    match = hash.match(/mode=([^&]+)/)
    requested = match && match[1] ? decodeURIComponent(match[1]) : null
  }

  if (requested && store.gameMode !== requested) {
  store.gameMode = requested
  store.startGame?.()
  return
  }

  if (store.gameState !== 'playing' || !Array.isArray(store.playerHand) || store.playerHand.length === 0) {
  store.startGame?.()
  }
  if (store.isCoop && (!Array.isArray(store.publicPool) || store.publicPool.length === 0)) {
  store.ensurePoolAtTurnStart?.()
  }
  } catch (_) {}
  },
  async prefetchDefinition(word){
  const key = String(word || '').toLowerCase()
  if (!key || key.length < 2) return
  if (this.definitionCache[key] || this.pendingDefinitions[key]) return
  const pending = this.fetchDefinitionPayload(key)
  this.pendingDefinitions[key] = pending
  try {
  const payload = await pending
  if (payload) this.definitionCache[key] = payload
  } finally {
  delete this.pendingDefinitions[key]
  }
  },
  async obtainDefinitionPayload(word){
  const key = String(word || '').toLowerCase()
  if (!key) return null
  if (this.definitionCache[key]) return this.definitionCache[key]

  if (this.pendingDefinitions[key]) {
  try {
  const cached = await this.pendingDefinitions[key]
  if (cached) {
  this.definitionCache[key] = cached
  return cached
  }
  } catch (_) {}
  }

  const pending = this.fetchDefinitionPayload(key)
  this.pendingDefinitions[key] = pending
  try {
  const payload = await pending
  if (payload) this.definitionCache[key] = payload
  return payload
  } finally {
  delete this.pendingDefinitions[key]
  }
  },
  async fetchDefinitionPayload(word){
  // Use Free Dictionary API (https://dictionaryapi.dev/)
  const endpoint = 'https://api.dictionaryapi.dev/api/v2/entries/en/' + encodeURIComponent(word)
  try {
  const res = await fetch(endpoint)
  if (!res || !res.ok) {
  return { word, entries: [], phonetic: '', rawText: '', error: true }
  }
  const data = await res.json()

  // Parse the Free Dictionary API response
  const entries = []
  if (Array.isArray(data) && data.length > 0) {
    const entry = data[0]
    if (Array.isArray(entry.meanings)) {
      entry.meanings.forEach(meaning => {
        const pos = meaning.partOfSpeech || ''
        if (Array.isArray(meaning.definitions)) {
          meaning.definitions.forEach(def => {
            entries.push({
              pos: pos,
              def: def.definition || '',
            })
          })
        }
      })
    }
  }

  const phonetic = (data?.[0]?.phonetic || '')
  return {
    word,
    entries: entries.filter(e => e.def),
    phonetic: String(phonetic),
    rawText: '',
    error: !entries.length && !phonetic,
  }
  } catch (_) {
  return { word, entries: [], phonetic: '', rawText: '', error: true }
  }
  },
  applyDefinitionPayload(word, payload){
  const entries = Array.isArray(payload?.entries) ? payload.entries.filter(e => e && e.def) : []
  this.moreState.lastWord = word
  this.moreState.expanded = false
  this.moreState.all = entries

  if (entries.length) {
  const first = entries[0]
  const mapped = this.mapPos(first.pos || '')
  this.definitionState.firstPos = mapped
  const head = (mapped ? `(${mapped}) ` : '') + first.def
  this.definitionState.text = this.clampText(head, 180)
  } else {
  this.definitionState.firstPos = ''
  const fallback = (payload?.rawText || '').trim()
  this.definitionState.text = fallback || t('dict.unavailable')
  }
  this.definitionState.phonetic = payload?.phonetic || ''
  },
  isCardSelected(card) {
  if (!card) return false
  const list = Array.isArray(this.gameStore?.selectedCards) ? this.gameStore.selectedCards : []
  return list.some(c => c && c.id === card.id)
  },
  selectCard(card) {
  if (!card || !card.id) return
  // 确保传递的card对象有有效的ID
  const safeCard = { ...card, id: String(card.id) }
  try { this.gameStore?.selectCard?.(safeCard) } catch (e) {}
  const list = Array.isArray(this.gameStore?.selectedCards) ? this.gameStore.selectedCards : []
  this.currentWord = list.map(c => c && c.letter).filter(Boolean).join('')
  },
  removeSelectedCard(idx) {
  const list = Array.isArray(this.gameStore?.selectedCards) ? this.gameStore.selectedCards : []
  if (idx>=0 && idx<list.length) list.splice(idx,1)
  this.currentWord = list.map(c => c && c.letter).filter(Boolean).join('')
  },
  mapPos(pos) {
  if (!pos) return ''
  const p = String(pos).toLowerCase()
  if (p.includes('noun')) return 'n.'
  if (p.includes('verb')) return 'v.'
  if (p.includes('adjective') || p.startsWith('adj')) return 'adj.'
  if (p.includes('adverb') || p.startsWith('adv')) return 'adv.'
  if (p.includes('pronoun')) return 'pron.'
  if (p.includes('preposition')) return 'prep.'
  if (p.includes('conjunction')) return 'conj.'
  if (p.includes('interjection')) return 'interj.'
  return p
  },
  clampText(s, n = 180) {
  const x = (s || '').replace(/\s+/g, ' ').trim()
  return x.length > n ? x.slice(0, n - 1) + '…' : x
  },
  async playWord() {
  const sel = (this.gameStore?.selectedCards || []).map(c => String(c?.letter || '').toLowerCase()).join('')
  let word = String(this.currentWord || '').trim().toLowerCase()
  if (!word) word = sel
  if (!word) return

          const ok = await this.gameStore?.playWord?.(word)
          this.definitionState = { visible: false, loading: false, text: '', firstPos: '', phonetic: '' }
          this.moreState = { expanded: false, lastWord: '', all: [] }
          if (!ok) { this.currentWord = ''; return }

          this.definitionState.visible = true
          this.definitionState.loading = true
          try {
            const payload = await this.obtainDefinitionPayload(word)
            if (payload) {
              this.applyDefinitionPayload(word, payload)
            } else {
              this.definitionState.firstPos = ''
              this.definitionState.text = t('dict.unavailable')
              this.definitionState.phonetic = ''
              this.moreState.lastWord = word
              this.moreState.all = []
            }
          } catch (_) {
            this.definitionState.firstPos = ''
            this.definitionState.text = t('dict.unavailable')
            this.definitionState.phonetic = ''
            this.moreState.lastWord = word
            this.moreState.all = []
          } finally {
            this.definitionState.loading = false
            this.$nextTick(() => {
              try { (this.$el.querySelector('.definition-panel') || {}).scrollIntoView?.({ block: 'nearest', behavior: 'smooth' }) } catch (_) {}
            })
          }
          this.currentWord = ''
        },
        async toggleMore() {
          if (!this.moreState.lastWord) return
          this.moreState.expanded = !this.moreState.expanded
          if (this.moreState.expanded && Array.isArray(this.moreState.all) && this.moreState.all.length) {
            const lines = this.moreState.all.map((d, i) => {
              const mapped = this.mapPos(d.pos || '')
              const head = mapped ? `(${mapped}) ` : ''
              return `${i + 1}. ${head}${d.def}`
            })
            this.definitionState.text = lines.join('\n')
          } else {
            const first = this.moreState.all[0]
            const mapped = this.mapPos(first?.pos || '')
            const firstLine = first ? ((mapped ? `(${mapped}) ` : '') + first.def) : ''
            this.definitionState.text = first ? this.clampText(firstLine, 180) : t('dict.unavailable')
          }
        },
        passTurn() { this.gameStore?.passTurn?.('manual') },
        restartGame() {
          this.definitionState = { visible: false, loading: false, text: '', firstPos: '', phonetic: '' }
          this.moreState = { expanded: false, lastWord: '', all: [] }
          this.currentWord = ''
          this.gameStore?.restart?.()
        },
        donateToPool() {
          if (!this.canDonate) return
          this.gameStore?.donateToPool?.()
          this.currentWord = ''
        },
      },

  }
  </script>
    <style scoped>
    :root { --brand: var(--color-primary-orange, #FF6B35); --muted: var(--color-text-secondary, #667085); --card: var(--color-bg-white, #FFFFFF); }

    .gb-debug { color:var(--color-text-secondary); margin-bottom:8px; }

    /* 版心与左右留白 */
    .container { max-width: 1200px; margin: 0 auto; padding-left: 24px; padding-right: 24px; }
    @media (max-width: 860px) { .container { padding-left: 16px; padding-right: 16px; } }

    .game-board { display:flex; flex-direction:column; gap:16px; padding-top:20px; padding-bottom:16px; }

    /* Header */
    .game-header { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; }
    .game-info h2 { margin: 0 0 6px 0; }
    .mode-title { display:flex; align-items:center; gap:10px; }
    .participants { display:inline-flex; align-items:center; gap:8px; font-weight:600; color:var(--color-text-secondary); }
    .participants .dot { opacity:.6; }
    .avatar { width:22px; height:22px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; font-size:12px; }
    .avatar.me { background:rgba(255, 107, 53, 0.15); color:var(--color-primary-orange); }
    .avatar.tm { background:#E7F8F3; color:#065F46; }
    .game-info p { margin: 0; color: var(--muted); }
    .meta { margin-left: 6px; color: var(--muted); }
    .timer { margin-left: 8px; }
    .timer.critical { color: var(--color-danger, #EF4444); font-weight: 700; }
    .coop-target { margin-left: 8px; }

    /* Right top */
    .header-right { display:flex; flex-direction:column; align-items:flex-end; gap:10px; min-width: 260px; margin-bottom: 8px; }
    .header-actions { display:flex; gap:12px; align-items:center; justify-content:flex-end; width:100%; }
    .btn-secondary { background:#fff; border:1px solid #D0D5DD; color:#344054; padding:8px 14px; border-radius:8px; font-weight:600; cursor:pointer; font-size:14px; transition:all var(--transition-base) var(--ease-in-out); }
    .btn-secondary:hover { border-color:var(--color-primary-orange, #FF6B35); color:var(--color-primary-orange, #FF6B35); }
    .btn-exit { padding:6px 12px; }
    .score-display { display:flex; gap:10px; flex-wrap:wrap; justify-content:flex-end; width:100%; }
    .score-item { background:#FFFFFF; border:1px solid #EAECF0; border-radius:12px; padding:10px 12px; min-width:120px; display:flex; align-items:center; justify-content:space-between; }
    .player-name { color:#475467; }
    .player-score { font-weight: 800; }

    /* Grid */
    .game-main { display:grid; grid-template-columns: 1.4fr 1fr 1fr; gap:28px; }
    .right-col { margin-top: 8px; }
    .left-col, .center-col, .right-col { display:flex; flex-direction:column; gap:14px; }

    .hand-area, .pool-area, .history, .selected-area, .controls {
      background: var(--card); border: 1px solid #EAECF0; border-radius: 16px; padding: 18px;
    }

    /* 公共池更高 + 来源角标（内部右上角） */
    .pool-area { min-height: 260px; margin-top: 20px; }
    .pool-area .cards-container { display:flex; flex-wrap:wrap; gap:12px 12px; padding-bottom:12px; }
    .pool-card { position: relative; display:inline-block; margin-bottom: 8px; }
    .pool-card .mini-card { position: relative; padding-top:14px; }
    .source-badge { position:absolute; top:-4px; right:2px; transform: translate(2px,2px); background:#0B1020; color:#FFFFFF; border-radius:8px; padding: 2px 6px; font-size:10px; line-height:1; border:none;
  box-shadow:0 1px 2px rgba(16,24,40,0.10); }
    .source-badge.deck { background:rgba(255, 107, 53, 0.2); color:var(--color-primary-orange, #FF6B35); }
    .source-badge.teammate { background:rgba(76, 175, 80, 0.2); color:#2E7D32; }

    .cards-container { display:flex; flex-wrap:wrap; gap:8px; }
    .mini-card { width:56px; height:78px; border-radius:10px; border:1px solid #EAECF0; background:#fff; display:flex; flex-direction:column; align-items:center; justify-content:center; padding-top:6px;
  padding-bottom:6px; box-shadow:0 2px 6px rgba(16,24,40,0.06); cursor:pointer; }
    .mini-card .ch { font-size:22px; font-weight:800; color:#101828; line-height:1; }
    .mini-card .pv { font-size:12px; color:#98A2B3; margin-top:4px; }
    .mini-card.selected { outline:2px solid var(--color-primary-orange, #FF6B35); }

    .restart-row-centered { display:flex; justify-content:center; }
    .btn-restart {
      background:var(--color-primary-orange, #FF6B35); color:#fff; border:none; padding:10px 16px; border-radius:10px; font-weight:800; cursor:pointer;
      box-shadow: 0 2px 4px rgba(255, 107, 53, 0.3);
    }
    .btn-restart:hover { filter: brightness(1.1); }

    .selected-area .selected-cards { display:flex; gap:8px; flex-wrap:wrap; }
    .selected-hint { margin: 12px 0 0; color: var(--muted); }
    .coop-hint.under-selected { margin-top: 6px; color:#667085; font-size: 13px; line-height: 1.5; }

    .controls .word-input { width:100%; padding:12px 14px; background:#FFFFFF; color:#101828; border:1px solid #D0D5DD; border-radius:12px; outline:none; }
    .buttons-row { display:flex; gap:10px; margin-top:10px; align-items:center; flex-wrap: wrap; }

    .btn-pass {
      background:var(--color-primary-orange, #FF6B35); color:#fff; border:none; padding:10px 14px; border-radius:10px; font-weight:800; cursor:pointer;
      box-shadow: 0 2px 4px rgba(255, 107, 53, 0.3);
    }
    .btn-pass:hover { filter: brightness(1.1); }

    .btn-primary { background:var(--color-primary-orange, #FF6B35); color:#fff; border:none; padding:12px 16px; border-radius:12px; font-weight:700; cursor:pointer; }
    .btn-primary:disabled { opacity:.6; cursor:not-allowed; }

    /* Definition 精致版 */
    .definition-panel { margin-top: 24px; padding: 14px 16px; background:#fff; border:1px solid #EAECF0; border-radius:12px; }
    .def-header { display:flex; align-items:baseline; justify-content:space-between; margin-bottom:6px; }
    .def-title { font-weight:800; color:#101828; }
    .def-meta { color:#667085; font-size:13px; }
    .def-word { font-weight:700; color:#1F2937; margin-right:6px; }
    .def-phonetic { color:#98A2B3; }

    .def-loading { color:#667085; }
    .def-body { display:flex; flex-direction:column; gap:8px; line-height:1.6; color:#344054; }
    .def-item { display:flex; align-items:flex-start; gap:8px; }
    .def-item.expanded { padding:6px 0; border-bottom:1px solid #F2F4F7; }
    .def-item.expanded:last-child { border-bottom:none; }

    .def-bullet {
      width:22px; height:22px; min-width:22px; border-radius:50%;
      background:rgba(255, 107, 53, 0.1); color:var(--color-primary-orange, #FF6B35); font-weight:700; display:inline-flex; align-items:center; justify-content:center;
      font-size:12px; margin-top:2px;
    }
    .pos-tag {
      display:inline-block; border-radius:999px; padding:2px 8px; font-size:12px; font-weight:700;
      color:#0B1020; background:#EEF2F6; text-transform:lowercase;
    }
    .pos-tag[data-pos*="noun"] { background:rgba(255, 107, 53, 0.1); color:var(--color-primary-orange, #FF6B35); }
    .pos-tag[data-pos*="verb"] { background:#E8FFF1; color:#10B981; }
    .pos-tag[data-pos*="adj"]  { background:#FFF4E5; color:#F59E0B; }
    .pos-tag[data-pos*="adv"]  { background:#F8E7FF; color:#A855F7; }

    .def-text { white-space:pre-wrap; }

    .def-actions { margin-top:8px; }
    .btn-link { background:none; border:none; color:var(--color-primary-orange, #FF6B35); cursor:pointer; padding:4px 8px; }

    /* History */
    .history-list { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:8px; }
    .history-list li { display:flex; align-items:center; justify-content:space-between; gap:12px; }
    .hist-word { font-weight:700; }
    .hist-score { color:var(--color-primary-orange, #FF6B35); font-weight:700; }
    .actor-tag { display:inline-block; font-size:12px; padding:2px 6px; border-radius:999px; margin-right:6px; }
    .actor-tag.me { background:rgba(255, 107, 53, 0.1); color:var(--color-primary-orange, #FF6B35); }
    .actor-tag.tm { background:#E7F8F3; color:#065F46; }

    /* Overlay */
    .overlay { position:fixed; inset:0; background: rgba(0,0,0,0.55); display:flex; align-items:center; justify-content:center; }
    .overlay-card { background:#FFFFFF; border:2px solid var(--color-primary-orange, #FF6B35); border-radius:16px; padding: 20px; width: min(460px, 92%); text-align:center; }

    /* Team progress */
    .team-progress { margin-top:8px; }
    /* 与手牌区拉开更大间距，避免贴近 */
    .team-progress { margin-bottom: 10px; }
    .team-progress .bar { height:10px; background:#EEF2F6; border-radius:999px; overflow:hidden; }
    .team-progress .fill { height:100%; background:var(--color-primary-orange, #FF6B35); width:0; transition: width .35s ease; }
    .team-progress .meta-line { margin-top:6px; color:#667085; font-size:13px; }

    @media (max-width: 980px) {
      .game-main { grid-template-columns: 1fr; }
      .header-right { align-items:flex-start; }
      .score-display { justify-content:flex-start; }
    }
    </style>
